import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { AutocompleteAddressInput } from 'components/autocomplete-address-input';
import { useAddressPredictions } from 'hooks/geolocation/use-address-predictions';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { hideKeyboard } from 'utils/keyboard';

import { ISearchBarProps } from './types';

export const SearchBar = ({
  autoFocus,
  onAddressSelected,
  value,
  onInputValueChange,
}: ISearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();

  const enableMapFallback = useFlag(LaunchDarklyFlag.ENABLE_MAP_FALLBACK);
  const enableAutomaticLocationShareAndKbClose =
    useFlag(LaunchDarklyFlag.ENABLE_AUTOMATIC_LOCATION_SHARE_AND_KEYBOARD_CLOSE) ?? true;

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, pathname]);

  const { predictions } = useAddressPredictions({
    pieceOfAddress: value,
    skip: !enableMapFallback,
  });

  const onAddressSelectedHandler = (address: string, placeId?: string) => {
    onAddressSelected(address, placeId);
    if (enableAutomaticLocationShareAndKbClose) {
      hideKeyboard();
      inputRef.current?.blur();
    }
  };

  return (
    <AutocompleteAddressInput
      autoFocus={autoFocus}
      required
      value={value}
      onInputValueChange={onInputValueChange}
      onAddressSelected={onAddressSelectedHandler}
      label={formatMessage({ id: 'yourAddress' })}
      aria-label={formatMessage({ id: 'enterAddressOrDrag' })}
      ref={inputRef}
      predictions={predictions}
      data-testid="storelocator-autocomplete"
    />
  );
};
